import { ApiError } from "@carepass/api-client";
import { useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminSession } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import type { Schemas } from "../workspace/api.generated";
import { StateNotice } from "../workspace/shared";
import { IntegrationView } from "./components/IntegrationView";
import { useIntegrationMutation, useProviderConfiguration, useProviderCredentialHistory } from "./queries";
import type { IntegrationMutation } from "./queries";

type Notice = { readonly tone: "information" | "danger"; readonly text: string };

function stateOf(query: { isPending: boolean; isError: boolean }) {
  return query.isPending ? "loading" as const : query.isError ? "error" as const : "ready" as const;
}

function detailState(query: ReturnType<typeof useProviderConfiguration>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function IntegrationDetailPageForId({ configurationId }: { readonly configurationId: number }) {
  const session = useAdminSession();
  const detail = useProviderConfiguration(configurationId);
  const history = useProviderCredentialHistory(configurationId);
  const mutation = useIntegrationMutation();
  const navigation = useAdminNavigation({
    contextKey: "integrations",
    defaultListPath: "/integrations?page=0",
    originLabel: "연동 설정 상세",
  });
  const [notice, setNotice] = useState<Notice>();
  const authorities = session.authSession?.authorities ?? [];

  const runMutation = async (input: IntegrationMutation) => {
    if (input.kind === "create") throw new Error("Provider configuration creation belongs to the list page.");
    try { return await mutation.mutateAsync(input); }
    finally { if (input.kind === "credential") mutation.reset(); }
  };

  const complete = async (input: IntegrationMutation, result: unknown) => {
    if (input.kind === "credential") {
      const [detailResult, historyResult] = await Promise.all([detail.refetch(), history.refetch()]);
      setNotice({
        tone: detailResult.error || historyResult.error ? "danger" : "information",
        text: detailResult.error || historyResult.error
          ? "인증 정보 저장 응답은 받았지만 최신 설정 또는 마스킹 이력을 불러오지 못했습니다."
          : `설정 ID ${configurationId}의 인증 정보를 저장하고 마스킹 이력을 갱신했습니다.`,
      });
      return;
    }
    if (input.kind === "connection-test") {
      const tested = result as Schemas["ProviderConnectionTestResult"];
      await detail.refetch();
      setNotice({ tone: tested.successful ? "information" : "danger", text: `연결 테스트 ${tested.successful ? "성공" : "실패"} · ${tested.code} · ${tested.message}` });
      return;
    }
    if (input.kind === "activate") {
      const saved = result as Schemas["ProviderConfigurationView"];
      await detail.refetch();
      setNotice({ tone: "information", text: `${saved.configurationName} · 서버 설정 ID ${saved.id}가 활성화되었습니다.` });
    }
  };

  return <IntegrationView
    mode="detail"
    providerCode=""
    serviceType=""
    providerMode=""
    active=""
    page={0}
    size={20}
    configurationsState="ready"
    selectedId={configurationId}
    selected={detail.data}
    detailState={detailState(detail)}
    history={history.data}
    historyState={stateOf(history)}
    canManage={authorities.includes("PROVIDER_MANAGE") && authorities.includes("ROLE_SUPER")}
    notice={notice}
    detailHref={(id) => pathForAdminEntityScreen("29", id)}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    onFilterChange={() => undefined}
    onPageChange={() => undefined}
    onSelect={() => undefined}
    onRetryList={() => { void detail.refetch(); }}
    onRetryHistory={() => { void history.refetch(); }}
    onMutation={runMutation}
    onMutationComplete={complete}
  />;
}

export function IntegrationDetailPage() {
  const configurationId = parseAdminEntityId(useParams().configurationId);
  if (configurationId === null) return <StateNotice state="not-found" />;
  return <IntegrationDetailPageForId key={configurationId} configurationId={configurationId} />;
}
